'use client';

/**
 * useSmsOptInConsent — state + payload binding for the SMS / voice opt-in
 * disclosure pair.
 *
 * Centralizes the ~6 lines of state every lead-capture form would otherwise
 * carry inline. Keep this hook portal-agnostic: it owns no UI, no fetching,
 * no side effects — just two booleans (SMS + Voice) and matching change
 * handlers, plus a `payload` shape ready to spread into the submission body.
 *
 * **Submission gating policy:** Both checkboxes are OPTIONAL by default.
 * The platform stopped enforcing SMS consent at submit time as a product
 * call — manually-dialed live-agent calls and one-off transactional texts
 * triggered by user actions don't require prior express written consent
 * under TCPA. Tenants whose downstream comms include autodialed /
 * prerecorded SMS reminders (which DO require prior express written
 * consent) should opt back into the gate by passing
 * `{ smsConsentRequired: true }` when they need the legal defense-in-
 * depth — e.g., a tenant running an autodialed-SMS campaign.
 *
 * Promotional / marketing SMS is NOT part of this hook. Marketing SMS
 * requires a separately-registered campaign with the SMS provider and a
 * dedicated promotional opt-in surface. Bundling marketing into a
 * transactional opt-in is the most common carrier-rejection cause and is
 * why we keep this hook strictly transactional.
 *
 * ```tsx
 * // Default (both optional):
 * const consent = useSmsOptInConsent();
 *
 * // Strict mode (autodialed SMS — TCPA gate):
 * const consent = useSmsOptInConsent({ smsConsentRequired: true });
 *
 * <SmsOptInDisclosure
 *   companyName={branding.companyName}
 *   {...consent.bind}
 * />
 *
 * await submit({ ...values, ...consent.payload });
 * ```
 */

import { useCallback, useMemo, useState } from 'react';

export interface SmsOptInConsentBinding {
  smsConsent: boolean;
  onSmsConsentChange: (checked: boolean) => void;
  voiceConsent: boolean;
  onVoiceConsentChange: (checked: boolean) => void;
  /**
   * Set to `true` after a submit attempt with `smsConsent === false` and
   * `smsConsentRequired` enabled. Tells the disclosure component to draw
   * the error state (red border + "Required" helper text) on the SMS
   * checkbox. Cleared automatically when the user checks the box.
   */
  showValidationError: boolean;
  /**
   * Mirrors the hook's `smsConsentRequired` option so callers can spread
   * `consent.bind` directly onto `<SmsOptInDisclosure {...consent.bind} />`
   * and have the asterisk + native `required` attribute follow the same
   * source of truth. Defaults to `false`.
   */
  smsConsentRequired: boolean;
}

export interface UseSmsOptInConsentOptions {
  /**
   * When `true`, `validate()` blocks submission until the user has checked
   * the SMS consent box and the disclosure renders the asterisk + native
   * HTML `required` attribute. Defaults to `false`.
   */
  smsConsentRequired?: boolean;
}

export interface SmsOptInConsentPayload {
  smsConsentSms: boolean;
  smsConsentVoice: boolean;
}

export interface UseSmsOptInConsentResult {
  /** Spread directly onto `<SmsOptInDisclosure {...consent.bind} />`. */
  bind: SmsOptInConsentBinding;
  /** Spread into the form-submission payload (`smsConsentSms` + `smsConsentVoice`). */
  payload: SmsOptInConsentPayload;
  /** Imperatively reset both booleans to false (e.g., after a successful submit). */
  reset: () => void;
  /**
   * `true` when SMS consent has been given. Use as a quick read-only
   * check (e.g. `disabled={!consent.canSubmit}` on a submit button) when
   * SMS consent is required.
   */
  canSubmit: boolean;
  /**
   * Validate at submit time. Returns `true` when SMS consent has been
   * given. When `false`, sets `showValidationError` so the disclosure
   * draws the error state and the user can see why submission was
   * blocked. Call this at the top of the form's submit handler:
   *
   *   if (collectsPhone && !smsConsent.validate()) return;
   */
  validate: () => boolean;
}

export function useSmsOptInConsent(options: UseSmsOptInConsentOptions = {}): UseSmsOptInConsentResult {
  const { smsConsentRequired = false } = options;
  const [smsConsent, setSmsConsentState] = useState(false);
  const [voiceConsent, setVoiceConsent] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);

  // Wrap the SMS setter so toggling the checkbox after a failed submit
  // clears the error state — feels right to the user (they fixed it, the
  // red goes away) and matches conventional form behavior.
  const setSmsConsent = useCallback((next: boolean) => {
    setSmsConsentState(next);
    if (next) setShowValidationError(false);
  }, []);

  const reset = useCallback(() => {
    setSmsConsentState(false);
    setVoiceConsent(false);
    setShowValidationError(false);
  }, []);

  const validate = useCallback(() => {
    // When the tenant hasn't opted into the strict TCPA gate, validate is
    // a no-op pass — submission proceeds regardless of consent state, and
    // the recorded `smsConsentSms` boolean still flows into the payload
    // so downstream comms can honor the user's actual choice.
    if (!smsConsentRequired) {
      setShowValidationError(false);
      return true;
    }
    if (!smsConsent) {
      setShowValidationError(true);
      return false;
    }
    setShowValidationError(false);
    return true;
  }, [smsConsent, smsConsentRequired]);

  const bind = useMemo<SmsOptInConsentBinding>(
    () => ({
      smsConsent,
      onSmsConsentChange: setSmsConsent,
      voiceConsent,
      onVoiceConsentChange: setVoiceConsent,
      showValidationError,
      smsConsentRequired,
    }),
    [smsConsent, voiceConsent, showValidationError, setSmsConsent, smsConsentRequired],
  );

  const payload = useMemo<SmsOptInConsentPayload>(
    () => ({
      smsConsentSms: smsConsent,
      smsConsentVoice: voiceConsent,
    }),
    [smsConsent, voiceConsent],
  );

  return {
    bind,
    payload,
    reset,
    // Without the strict gate, every state qualifies for submission. With
    // the gate, the user must have checked the SMS box.
    canSubmit: smsConsentRequired ? smsConsent : true,
    validate,
  };
}

export default useSmsOptInConsent;
